import {
  BookingSummary as AccountBookingSummary,
  BookingCancellationReason,
  BookingCancellationVerdict,
} from "../../api/account/types";
import { BookingSummary as BookingSummaryProps } from "../../types/bookingCreation";

/**
 * #1495 / M10 self-cancel: what the bookings tab shows for the selected
 * booking. It READS the server's verdict (`cancellation` on `/me/bookings`,
 * `bookerCancelVerdict` in new-backend/app) and never re-derives it — the
 * server enforces, the client explains (ADR-0020). Before M10 this file held a
 * copy of the rule; that copy is gone, so there is nothing here to drift.
 *
 * Kept out of OrdersPage because that component function sits at Sonar's
 * cognitive-complexity ceiling — and so the mapping is testable without
 * mounting the page.
 */

/** What the confirm dialog says the cancellation costs. */
export type SelfCancelTerms =
  | { kind: "free_until"; freeUntil: string }
  /** Covered by the organisation: free until the start under ITS agreement, not the AGB. */
  | { kind: "covered"; freeUntil: string }
  /** Nothing paid, no contract: withdrawn free of charge (PO, 2026-10-19). */
  | { kind: "withdraw" }
  | { kind: "free" }
  | { kind: "agb" }
  /** A tier is in force (W1-6, PO F4): cancelling charges this fee, which the booker confirms. */
  | { kind: "fee"; percent: number; amount: number };

export interface SelfCancelState {
  eligible: boolean;
  canceledNote: boolean;
  /**
   * With `canceledNote`: the fee the cancel recorded under the booking's rule
   * (0 = no charges); null = no rule decided it, so the note says nothing
   * about costs.
   */
  canceledFee: number | null;
  bookingNumber: string;
  /** Set when eligible: the terms the dialog states. */
  terms: SelfCancelTerms | null;
  /**
   * Set when the booking is NOT self-cancellable and the tab says why, with
   * the way to the operator — never a silently missing button. `unknown` = a
   * backend that sent no verdict. Not set for a booking that already started:
   * a past booking is not one anyone looks to cancel.
   */
  refusal: SelfCancelRefusal | null;
}

/** The refusals the tab explains — each has an `orders.cancel_booking.refusal_*` text. */
export const SELF_CANCEL_REFUSALS = [
  "not_self_cancelable",
  "invoice_issued",
  "fee_applies",
  "price_unknown",
  "rule_unreadable",
  "not_found",
  // The fee changed between the dialog and the cancel (W1-6).
  "fee_changed",
  // The cancel route's answers when the booking changed between list and
  // click: 409 already_canceled / booking_changed, 422 already_started.
  "already_canceled",
  "already_started",
  "booking_changed",
  "unknown",
] as const;

/**
 * The text key for a refusal the server named — on the list's verdict or in
 * a failed cancel's `error.data.code` — so the screen never shows the
 * server's English sentence (review of #3394).
 */
export const refusalTextKey = (code: unknown): string =>
  `orders.cancel_booking.refusal_${
    (SELF_CANCEL_REFUSALS as readonly unknown[]).includes(code) ? code : "unknown"
  }`;
export type SelfCancelRefusal = (typeof SELF_CANCEL_REFUSALS)[number];

/** A refusal the server named; anything else (a newer server's reason) reads as `unknown`. */
const refusalOf = (reason: BookingCancellationReason): SelfCancelRefusal =>
  (SELF_CANCEL_REFUSALS as readonly string[]).includes(reason)
    ? (reason as SelfCancelRefusal)
    : "unknown";

const NONE: SelfCancelState = {
  eligible: false,
  canceledNote: false,
  canceledFee: null,
  bookingNumber: "",
  terms: null,
  refusal: null,
};

const termsOf = (verdict: BookingCancellationVerdict): SelfCancelTerms => {
  const { reason, freeUntil } = verdict;
  if (reason === "fee" && verdict.fee && verdict.feePercent !== null) {
    return { kind: "fee", percent: verdict.feePercent, amount: verdict.fee.amount };
  }
  if (reason === "no_rule") return { kind: "agb" };
  if (reason === "request") return { kind: "withdraw" };
  if (reason === "covered" && freeUntil) return { kind: "covered", freeUntil };
  if (reason === "free_of_charge" || !freeUntil) return { kind: "free" };
  return { kind: "free_until", freeUntil };
};

export const deriveSelfCancelState = (
  isAccountMode: boolean,
  selectedBooking: BookingSummaryProps | null,
  accountBookings: AccountBookingSummary[] | undefined
): SelfCancelState => {
  if (!isAccountMode || !selectedBooking) return NONE;
  const accountBooking = accountBookings?.find(
    (booking) => booking.id === selectedBooking.id
  );
  if (!accountBooking) return NONE;
  if (accountBooking.isCanceled) {
    return { ...NONE, canceledNote: true, canceledFee: accountBooking.cancellationFee ?? null };
  }
  const verdict = accountBooking.cancellation;
  if (!verdict) return { ...NONE, refusal: "unknown" };
  if (verdict.reason === "already_canceled") return { ...NONE, canceledNote: true };
  if (verdict.reason === "already_started") return NONE;
  // A station or handover booking: the self-cancel is not offered there yet
  // (#3042), so the tab says nothing rather than "cannot be cancelled here".
  if (verdict.reason === "not_offered") return NONE;
  if (!verdict.selfCancelable) return { ...NONE, refusal: refusalOf(verdict.reason) };
  // A fee the server did not state is not one to confirm: never "free" by default.
  if (verdict.reason === "fee" && (!verdict.fee || verdict.feePercent === null)) {
    return { ...NONE, refusal: "unknown" };
  }
  return {
    ...NONE,
    eligible: true,
    bookingNumber: selectedBooking.bookingNumber ?? "",
    terms: termsOf(verdict),
  };
};
